import { RejectReason } from 'src/app/shared/models/RejectReason';
import { Order } from 'src/app/shared/models/orders';

export type AridosDetailInitialStep = 'summary' | 'observations';
export type AridosObservationBackStep = 'summary' | 'list';
export type AridosRejectionBackStep = 'summary' | 'observations';

/**
 * Datos que necesita la UI del despacho Áridos.
 *
 * Los componentes y templates consumen esta proyección. La respuesta legacy
 * conserva `neg` para los contratos existentes, pero su interpretación queda
 * confinada a este archivo.
 */
export interface AridosDispatchPresentation {
  destinationLabel: 'Destinatario' | 'Obra';
  referenceLabel: 'Entrega' | 'N° de entrega' | 'N° de pedido';
  sealLabel: 'Numero de transporte:' | 'N° de sello:';
  showLoadingInfo: boolean;
  showGuideInstruction: boolean;
  showOperationalDetails: boolean;
  showTotalAmount: boolean;
  showCone: boolean;
  showSeal: boolean;
  showSample: boolean;
  showDocument: boolean;
  showTruckStatus: boolean;
  initialStep: AridosDetailInitialStep;
  observationBackStep: AridosObservationBackStep;
  rejectionBackStep: AridosRejectionBackStep;
  returnReasonGroup: string;
}

/**
 * Traduce las variantes actuales del despacho Áridos a propiedades de UI.
 * `3100`, `3400`, `3200` y `5000` se mantienen como reglas de compatibilidad;
 * ningún componente necesita conocer esos códigos.
 */
export function getAridosDispatchPresentation(
  order: Pick<Order, 'neg'> | null | undefined,
): AridosDispatchPresentation {
  const businessCode = String(order?.neg ?? '');
  const isRecipientDelivery = businessCode === '3100';
  const isTransportDelivery = businessCode === '3400';
  const hasConcreteStyleFields = !isRecipientDelivery && !isTransportDelivery;

  return {
    destinationLabel: isRecipientDelivery ? 'Destinatario' : 'Obra',
    referenceLabel: isTransportDelivery
      ? 'Entrega'
      : isRecipientDelivery
        ? 'N° de entrega'
        : 'N° de pedido',
    sealLabel: isTransportDelivery ? 'Numero de transporte:' : 'N° de sello:',
    showLoadingInfo: hasConcreteStyleFields,
    showGuideInstruction: isRecipientDelivery || isTransportDelivery,
    showOperationalDetails: hasConcreteStyleFields,
    showTotalAmount: hasConcreteStyleFields,
    showCone: hasConcreteStyleFields,
    showSeal: !isRecipientDelivery,
    showSample: !isRecipientDelivery,
    showDocument: false,
    showTruckStatus: true,
    initialStep: isTransportDelivery ? 'summary' : 'observations',
    observationBackStep: isTransportDelivery ? 'summary' : 'list',
    rejectionBackStep: isTransportDelivery ? 'summary' : 'observations',
    returnReasonGroup: businessCode,
  };
}

function reasonText(reason: unknown): string {
  if (typeof reason === 'string') return reason;
  if (!reason || typeof reason !== 'object') return String(reason ?? '');
  const item = reason as Record<string, unknown>;
  return String(item.description ?? item.DESCRIPCION ?? item.descripcion ?? item.name ?? '');
}

function reasonBusinessCode(reason: unknown): string {
  if (!reason || typeof reason !== 'object') return '';
  const item = reason as Record<string, unknown>;
  return String(item.neg ?? item.NEG ?? '');
}

/** Normaliza motivos de rechazo, tanto en formato agrupado como plano. */
export function getAridosRejectReasons(
  data: unknown,
  order: Pick<Order, 'neg'> | null | undefined,
): RejectReason[] {
  const group = getAridosDispatchPresentation(order).returnReasonGroup;

  if (Array.isArray(data)) {
    return data
      .filter((reason) => {
        const reasonGroup = reasonBusinessCode(reason);
        return !reasonGroup || reasonGroup === group;
      })
      .map((reason, index) => {
        const item = reason as Record<string, unknown>;
        return new RejectReason(
          Number(item?.id ?? item?.ID ?? index),
          reasonText(reason),
          group,
        );
      })
      .filter((reason) => !!reason.description);
  }

  if (!data || typeof data !== 'object') return [];
  const grouped = (data as Record<string, unknown>)[group];
  if (Array.isArray(grouped)) {
    return grouped
      .map((reason, index) => new RejectReason(index, reasonText(reason), group))
      .filter((reason) => !!reason.description);
  }
  if (!grouped || typeof grouped !== 'object') return [];

  return Object.entries(grouped as Record<string, unknown>)
    .map(([id, description]) => new RejectReason(Number(id), reasonText(description), group))
    .filter((reason) => !!reason.description);
}

/** Devuelve únicamente las descripciones para el selector de retorno. */
export function getAridosReturnReasonDescriptions(
  data: unknown,
  order: Pick<Order, 'neg'> | null | undefined,
): string[] {
  return getAridosRejectReasons(data, order)
    .map((reason) => reason.description)
    .filter(Boolean);
}
